import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { checkEmail, findImage, isPaymentTokenExpired } from 'utils/Utils';
import useModals from '../Modal/manager/useModal';
import { GlobalCard, Scheme } from 'app/pages/Payment/data';
import CustomModal from '../Modal/CustomModal';
import Credit3DsForm from '../Credit3DsForm';
import Credit2DForm from '../Credit2DForm';
import { usePaymentState } from 'payment_state/PaymentStateContext';
import CreditKoreaForm from '../CreditKoreaForm';
import ErrorModal from '../Modal/ErrorModal';
import TermsModal from '../Modal/TermsModal';
import PrivacyPolicyModal from '../Modal/PrivacyPolicyModal';
import { useNavigate } from 'react-router-dom';
import { RequestPV01Form } from '../PV01Form/types';
import { isMobile } from 'react-device-detect';
import { useMutation } from 'react-query';
import { innerSearch } from 'api/Api';
import ArrowPng from 'app/assets/image/common/arrow.png';
import { useMediaQuery } from 'react-responsive';
import { useLoadKeypadScripts } from 'app/hooks/useLoadKeypadScripts';
import LoadingIndicator from '../LoadingIndicator';

export interface PaymentProps {
  creditData: GlobalCard[];
  keypadToken: string;
  koreanCardData: Scheme[];
  vietnamCardData: Scheme[];
  selectedScheme: Scheme;
  setSelectedScheme: (method: Scheme) => void;
  setLoading: (method: boolean) => void;
  handleClosed: (e) => void;
  setPopup: (e: Window | null) => void;
  isGlobal: boolean;
}

const TabCredit = ({
  creditData,
  keypadToken,
  koreanCardData,
  vietnamCardData,
  selectedScheme,
  setSelectedScheme,
  setLoading,
  handleClosed,
  setPopup,
  isGlobal,
}: PaymentProps) => {
  const foucsTerms = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { openModal } = useModals();
  const navigate = useNavigate();

  let checkPopupTimer;
  const [card, setCard] = useState<string>('');
  const [credit, setCredit] = useState<string>('');
  const [isNext, setIsNext] = useState<boolean>(false);
  const [isUnique, setIsUnique] = useState<boolean>(false);
  const [brandList, setBrandList] = useState<string[]>([]);
  const [AgreeCheckError, setAgreeCheckError] = useState<boolean>(false);
  const [agreeCheck, setAgreeCheck] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [isEmailError, setEmailError] = useState(false);
  const [pv01RequestBody, setPv01RequestBody] = useState<RequestPV01Form>();
  const [open, setOpen] = useState<boolean>(false);
  const isMobileSize = useMediaQuery({ maxWidth: 767 });

  // 하단 다음 버튼 클릭시 동작
  const { paymentState, setPaymentState } = usePaymentState();

  const { isKeypadScriptReady } = useLoadKeypadScripts();

  useEffect(() => {
    //Brand목록 생성
    if (creditData) {
      const schemeCdList = creditData.map(item => item.schemeCd);
      setBrandList(schemeCdList);
    }

    // 자동 Next
    const lists = [
      { name: 'global', data: creditData },
      { name: 'korea', data: koreanCardData },
      { name: 'vietnam', data: vietnamCardData },
    ];
    const nonEmpty = lists.filter(list => list.data.length > 0);

    if (nonEmpty.length === 1) {
      setIsUnique(true);
      setCredit(nonEmpty[0].name);
      selectCardAction(nonEmpty[0].name);
      if (nonEmpty[0].name !== 'vietnam') {
        setIsNext(true);
      }
      setSelectedScheme({
        schemeDisplayNm: '',
        partnerCurr: '',
        schemeCd: '',
        schemeGroup: '',
        approvalAmount: 0,
      });
    }
  }, [creditData, koreanCardData, vietnamCardData]);

  const handleNext = e => {
    e.preventDefault();
    if (card === '') {
      // apm을 선택하지 않은 경우
      openModal(ErrorModal, {
        message: t('error_card_select'),
      });
      return;
    } else {
      if (card !== 'vietnam') {
        setCredit(card);
        setIsNext(true);
      } else {
        if (!agreeCheck) {
          setAgreeCheckError(true);
          openModal(ErrorModal, {
            message: t('error_agree_check'),
          });
          foucsTerms.current?.scrollIntoView({ behavior: 'smooth' });
          return;
        }
        // setAgreeCheck(false);
        handlePayNow(e);
      }
    }
  };

  // 카드 선택시 동작 (2D, 3Ds, Korea)
  const selectCardAction = selectCard => {
    setCard(selectCard);
  };

  const openPreviousMessage = e => {
    e.preventDefault();
    openModal(CustomModal, {
      message: t('move_previous_message'),
      isShowTwoButton: true,
      onSubmit: () => {
        setCredit('');
        setCard('');
        setIsNext(false);
        setSelectedScheme({
          schemeDisplayNm: '',
          partnerCurr: '',
          schemeCd: '',
          schemeGroup: '',
          approvalAmount: 0,
        });
      },
    });
  };

  const handleTermsOrPrivacy = content => {
    if (content === 'SERVICE') {
      openModal(TermsModal, {
        onSubmit: () => {},
      });
    } else {
      openModal(PrivacyPolicyModal, {
        onSubmit: () => {},
      });
    }
  };

  useEffect(() => {
    setAgreeCheckError(false);
  }, [agreeCheck]);

  // InnerSearch 실행
  const transactResult = useMutation(() => innerSearch(paymentState.tid), {
    onSuccess: data => {
      setLoading(false);

      // InnerSearch 결과처리
      payResult(data);

      // 결과 상태가 없을경우 예외처리
      if (
        data.data.response.resultStatus === null ||
        data.data.response.resultStatus === undefined
      ) {
        navigate('/error');
      }
    },
    onError: error => {
      setLoading(false);
      navigate('/error');
    },
  });

  // 결제 결과 PaymentState set 및 결과화면 처리
  const payResult = data => {
    // 결과 상태
    const resultStatus = String(data.data.response.resultStatus).toLowerCase();
    // PayType
    const payType = String(data.data.display.payType).toLowerCase();
    setPaymentState(prevState => ({
      ...prevState,
      resultCode: data.data.response.resultCode,
      resultMessage: data.data.response.resultMessage,
      tid: data.data.response.tid,
      orderId: data.data.response.orderId,
      mallReserved: data.data.response.mallReserved,
      // returnUrl: data.data.returnUrl,
      descriptor: data.data.display.descriptor,
      schemeGroup: data.data.response.schemeGroup,
      schemeCode: data.data.response.schemeCode,
    }));

    // Status에 따른 Paging 처리
    switch (resultStatus) {
      case 'success':
        navigate('/success');
        break;
      case 'decline':
        navigate('/decline');
        break;
      case 'processing':
        navigate('/processing');
        break;
      case 'pending':
        payType === 'vbank' || payType === 'otc'
          ? navigate('/vbankPending')
          : navigate('/pending');
        break;
      case 'failed':
        navigate('/failed');
        break;
      default:
        navigate('/error');
    }
  };

  const handlePayNow = e => {
    e.preventDefault();
    // 이메일 validation
    if (email !== '') {
      if (!checkEmail(email)) {
        setEmailError(true);
        openModal(ErrorModal, {
          message: t('check_email_format'),
        });
        return;
      }
      setEmailError(false);
    }

    if (isPaymentTokenExpired()) {
      // token expired
      navigate('/paymentExpired');
      return;
    }

    const submitFormData: RequestPV01Form = {
      schemeCode: selectedScheme.schemeCd,
      authorization: `Bearer ${paymentState.auth}`,
      amount: paymentState.requestAmount,
      clientKey: paymentState.clientKey,
      currency: paymentState.requestCurrency,
      mid: paymentState.mid,
      orderId: paymentState.orderId,
      productName: paymentState.productName,
      reqDate: paymentState.reqDate,
      webhookUrl: paymentState.webhookUrl,
      returnUrl: paymentState.returnUrl,
      sign: paymentState.sign,
      email: email,
      isMobile: isMobile ? 'Y' : 'N',
      tid: paymentState.tid,
      displayType: 'UI',
      mallReserved: paymentState.mallReserved,
    };
    setPv01RequestBody(submitFormData);
    openModal(CustomModal, {
      message: t('open_apm_app', { name: t('VIETNAMOnly') }),
      isShowTwoButton: true,
      onSubmit: () => {
        setLoading(true);
        if (isMobile) {
          navigate('/formSubmit?schemeGroup=PV01', {
            state: { formData: submitFormData },
          });
        } else {
          const popup = window.open(
            '/formSubmit?schemeGroup=PV01',
            '_blank',
            'width=700, height=850',
          );
          if (popup) {
            setTimeout(() => {
              popup?.postMessage(
                { eventType: 'formData', data: submitFormData },
                window.location.origin,
              );
            }, 1000);
          }
          checkPopupTimer = setInterval(checkPopupClosed, 1000, popup);
        }
      },
    });
  };

  function checkPopupClosed(popup) {
    if (popup && popup.closed) {
      clearInterval(checkPopupTimer);
      const ErrorKey = window.sessionStorage.getItem('isError');
      if (ErrorKey !== 'true') {
        transactResult.mutate();
      }
    }
  }

  return (
    <>
      {!isNext && (
        <div className="tab">
          <div
            id="tab-1"
            className="tab-on current"
            style={{
              height: isGlobal
                ? isMobileSize
                  ? 'calc((var(--vh, 1vh) * 100) - 371px)'
                  : card === 'vietnam'
                  ? 184
                  : 227
                : isMobileSize
                ? 'calc((var(--vh, 1vh) * 100) - 305px)'
                : card === 'vietnam'
                ? 250
                : 293,
            }}
          >
            {isKeypadScriptReady ? (
              <div className="card">
                {creditData && creditData.length > 0 && (
                  <>
                    <input type="radio" id="sort-card1" name="Boost" />
                    <label
                      htmlFor="sort-card1"
                      onClick={() => {
                        selectCardAction('global');
                        setSelectedScheme({
                          schemeDisplayNm: '',
                          partnerCurr: '',
                          schemeCd: '',
                          schemeGroup: '',
                          approvalAmount: 0,
                        });
                      }}
                    >
                      <div className="middle">
                        <div className="img">
                          <span className="card-off">
                            <img src={findImage('card.png')} />
                          </span>
                          <span className="card-on">
                            <img src={findImage('card-white.png')} />
                          </span>
                        </div>
                        <div className="name">{t('GlobalAll')}</div>
                      </div>
                    </label>
                  </>
                )}
                {koreanCardData && koreanCardData.length > 0 && (
                  <>
                    <input type="radio" id="sort-card2" name="Boost" />
                    <label
                      htmlFor="sort-card2"
                      onClick={() => {
                        selectCardAction('korea');
                        setSelectedScheme({
                          schemeDisplayNm: '',
                          partnerCurr: '',
                          schemeCd: '',
                          schemeGroup: '',
                          approvalAmount: 0,
                        });
                      }}
                    >
                      <div className="middle">
                        <div className="img">
                          <span className="card-off">
                            <img src={findImage('card-kr.png')} />
                          </span>
                          <span className="card-on">
                            <img src={findImage('card-kr-white.png')} />
                          </span>
                        </div>
                        <div className="name">{t('KOREAOnly')}</div>
                      </div>
                    </label>
                  </>
                )}
                {vietnamCardData && vietnamCardData.length > 0 && (
                  <>
                    <input type="radio" id="sort-card3" name="Boost" />
                    <label
                      htmlFor="sort-card3"
                      onClick={() => {
                        selectCardAction('vietnam');
                        setEmail('');
                        setSelectedScheme({
                          schemeDisplayNm: vietnamCardData[0].schemeDisplayNm,
                          partnerCurr: vietnamCardData[0].partnerCurr,
                          schemeCd: vietnamCardData[0].schemeCd,
                          schemeGroup: vietnamCardData[0].schemeGroup,
                          approvalAmount: vietnamCardData[0].approvalAmount,
                        });
                      }}
                    >
                      <div className="middle">
                        <div className="img">
                          <span className="card-off">
                            <img src={findImage('card-vn.png')} />
                          </span>
                          <span className="card-on">
                            <img src={findImage('card-vn-white.png')} />
                          </span>
                        </div>
                        <div className="name">{t('VIETNAMOnly')}</div>
                      </div>
                    </label>
                  </>
                )}
              </div>
            ) : (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <LoadingIndicator />
              </div>
            )}

            {card === 'vietnam' && (
              <div className="emailOptionBox">
                <div className="title">{t('payment_confirm_email_opt')}</div>
                <div className={isEmailError ? 'input error' : 'input'}>
                  <input
                    name="email"
                    type="email"
                    onChange={e => setEmail(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {credit === 'korea' && isNext && (
        <CreditKoreaForm
          data={koreanCardData}
          selectedScheme={selectedScheme}
          setSelectedScheme={setSelectedScheme}
          previous={openPreviousMessage}
          setLoading={setLoading}
          setPopup={setPopup}
          isUnique={isUnique}
          handleClosed={handleClosed}
          isGlobal={isGlobal}
        />
      )}
      {credit === 'global' && isNext && creditData[0].authType === '2D' && (
        <Credit2DForm
          previous={openPreviousMessage}
          keypadToken={keypadToken}
          setLoading={setLoading}
          brandList={brandList}
          isUnique={isUnique}
          handleClosed={handleClosed}
          isGlobal={isGlobal}
        />
      )}
      {credit === 'global' && isNext && creditData[0].authType === '3DS' && (
        <Credit3DsForm
          previous={openPreviousMessage}
          keypadToken={keypadToken}
          setLoading={setLoading}
          brandList={brandList}
          setPopup={setPopup}
          isUnique={isUnique}
          handleClosed={handleClosed}
          isGlobal={isGlobal}
        />
      )}
      {card === 'vietnam' ? (
        <>
          <div className="agreeFloatBox">
            <div className="label" ref={foucsTerms}>
              <div className="check">
                <input
                  type="checkbox"
                  id={AgreeCheckError ? 'checkbox error' : 'checkbox'}
                  name="agree"
                  checked={agreeCheck}
                  onChange={e => {
                    setAgreeCheck(e.target.checked);
                  }}
                />

                <label
                  htmlFor={AgreeCheckError ? 'checkbox error' : 'checkbox'}
                ></label>
              </div>
              <div className="columnBox">
                <div className="text">
                  <div
                    onClick={() => setAgreeCheck(!agreeCheck)}
                    style={{ cursor: 'pointer' }}
                  >
                    {t('start_agree_terms')}
                    {t('terms_and_conditions')}
                    {t('and_the')}
                    {t('privacy_policy')}
                    {t('end_agree_terms')}
                  </div>
                  <img
                    className={`arrow ${open ? 'open' : ''}`}
                    src={ArrowPng}
                    alt=""
                    onClick={() => setOpen(!open)}
                  />
                </div>
                <div className={`text toggleRow ${open ? 'open' : ''}`}>
                  <p className="dot">●</p>
                  <span
                    className="link"
                    onClick={() => handleTermsOrPrivacy('SERVICE')}
                  >
                    {t('terms_and_conditions')}
                  </span>
                </div>
                <div className={`text toggleRow ${open ? 'open' : ''}`}>
                  <p className="dot">●</p>
                  <span
                    className="link"
                    onClick={() => handleTermsOrPrivacy('PRIVACY')}
                  >
                    {t('privacy_policy')}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="c-section">
            <div className="box">
              <button className="all-close" onClick={handleClosed}>
                {t('cancel')}
              </button>
              <button className="orange" onClick={handleNext}>
                {t('pay_now')}
              </button>
            </div>
          </div>
        </>
      ) : (
        !isNext && (
          <div className="c-section" style={{ marginTop: 47 }}>
            <div className="box">
              <button className="all-close" onClick={handleClosed}>
                {t('cancel')}
              </button>
              <button className="orange" onClick={handleNext}>
                {t('next')}
              </button>
            </div>
          </div>
        )
      )}

      {/* {(credit === '' || card === 'vietnam') && (
        <>
          {card === 'vietnam' && (
            <div className="agreeFloatBox">
              <div className="label" ref={foucsTerms}>
                <div className="check">
                  <input
                    type="checkbox"
                    id={AgreeCheckError ? 'checkbox error' : 'checkbox'}
                    name="agree"
                    checked={agreeCheck}
                    onChange={e => {
                      setAgreeCheck(e.target.checked);
                    }}
                  />
                  <label
                    htmlFor={AgreeCheckError ? 'checkbox error' : 'checkbox'}
                  />
                </div>
                <div className="text">
                  {t('start_agree_terms')}
                  <span
                    className="link"
                    onClick={() => handleTermsOrPrivacy('SERVICE')}
                  >
                    {t('terms_and_conditions')}
                  </span>
                  {t('and_the')}
                  <span
                    className="link"
                    onClick={() => handleTermsOrPrivacy('PRIVACY')}
                  >
                    {t('privacy_policy')}
                  </span>
                  {t('end_agree_terms')}
                </div>
              </div>
            </div>
          )}
          <div className="c-section">
            <div className="box">
              <button className="all-close" onClick={handleClosed}>
                {t('cancel')}
              </button>
              <button className="orange" onClick={handleNext}>
                {card === 'vietnam' ? t('pay_now') : t('next')}
              </button>
            </div>
          </div>
        </>
      )} */}
    </>
  );
};

export default TabCredit;
